import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  X, Plus, Trash2, ChevronRight, ChevronDown, 
  Loader2, Flag, ArrowRight, ArrowLeft,
  Clipboard, Users, Package, User, PanelRightClose, PanelRightOpen,
  Link2, Link2Off, List, BarChart3, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { jobsAPI, teamMembersAPI, boqAPI } from '../utils/api';
import { formatCurrency, formatNumber } from '../utils/currency';

const ProgramModal = ({ isOpen, onClose, jobId, jobCode, canEdit }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const inputRef = useRef(null);

  // View toggle state (Phase 5)
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'gantt'

  // Resource/Material form state
  const [showAddResource, setShowAddResource] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [newResource, setNewResource] = useState({ member_id: '', planned_hours: 0 });
  const [newMaterial, setNewMaterial] = useState({ 
    category: 'Materials', 
    description: '', 
    quantity: 0, 
    unit: 'each',
    rate: 0
  });
  const [boqCategories, setBoqCategories] = useState([]);

  // Dependency form state (Phase 4)
  const [showAddDependency, setShowAddDependency] = useState(false);
  const [availablePredecessors, setAvailablePredecessors] = useState([]);
  const [newDependency, setNewDependency] = useState({ predecessor_id: '', dependency_type: 'FS', lag_days: 0 });
  const [loadingPredecessors, setLoadingPredecessors] = useState(false);

  // Load tasks when modal opens
  useEffect(() => {
    if (isOpen && jobId) {
      loadTasks();
      loadTeamMembers();
    }
  }, [isOpen, jobId]);

  // Focus input when editing
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const res = await jobsAPI.getTasks(jobId);
      setTasks(res.data.tasks || []);
      const allIds = new Set((res.data.tasks || []).map(t => t.id));
      setExpandedTasks(allIds);
    } catch (error) {
      toast.error('Failed to load project tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const [membersRes, categoriesRes] = await Promise.all([
        teamMembersAPI.getAll(),
        boqAPI.getCategories()
      ]);
      setTeamMembers((membersRes.data || []).filter(m => m.status === 'active'));
      setBoqCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to load team members or categories');
    }
  };

  // Build hierarchical tree for display
  const buildTaskTree = useCallback(() => {
    const taskMap = new Map(tasks.map(t => [t.id, { ...t, children: [] }]));
    const roots = [];
    
    tasks.forEach(task => {
      const node = taskMap.get(task.id);
      if (task.parent_id && taskMap.has(task.parent_id)) {
        taskMap.get(task.parent_id).children.push(node);
      } else {
        roots.push(node);
      }
    });
    
    const sortByOrder = (items) => {
      items.sort((a, b) => a.order - b.order);
      items.forEach(item => sortByOrder(item.children));
    };
    sortByOrder(roots);
    
    return roots;
  }, [tasks]);

  // Flatten tree for display with visibility based on expanded state
  const getFlattenedTasks = useCallback(() => {
    const result = [];
    let rowNum = 1;
    
    const flatten = (nodes, visible = true) => {
      nodes.forEach(node => {
        if (visible) {
          result.push({ ...node, rowNum: rowNum++ });
        }
        if (node.children.length > 0) {
          flatten(node.children, visible && expandedTasks.has(node.id));
        }
      });
    };
    
    flatten(buildTaskTree());
    return result;
  }, [buildTaskTree, expandedTasks]);

  const getSelectedTask = useCallback(() => {
    return tasks.find(t => t.id === selectedTaskId);
  }, [tasks, selectedTaskId]);

  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleAddTask = async (parentId = null) => {
    if (!canEdit) return;
    
    try {
      setSaving(true);
      const newTask = await jobsAPI.createTask(jobId, {
        title: 'New Task',
        parent_id: parentId,
        given_work: 0,
        work_unit: 'hours'
      });
      setTasks(prev => [...prev, newTask.data]);
      if (parentId) {
        setExpandedTasks(prev => new Set([...prev, parentId]));
      }
      setEditingCell({ taskId: newTask.data.id, field: 'title' });
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleInsertRow = async (taskId, position) => {
    if (!canEdit) return;
    
    try {
      setSaving(true);
      const newTask = await jobsAPI.insertTaskRow(jobId, taskId, position);
      await loadTasks();
      setEditingCell({ taskId: newTask.data.id, field: 'title' });
      toast.success('Row inserted');
    } catch (error) {
      toast.error('Failed to insert row');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canEdit) return;
    if (!window.confirm('Delete this task and all its subtasks?')) return;
    
    try {
      setSaving(true);
      await jobsAPI.deleteTask(jobId, taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId && t.parent_id !== taskId));
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
        setShowDetailPanel(false);
      }
      await loadTasks();
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    } finally {
      setSaving(false);
    }
  };

  const handleIndent = async (taskId, direction) => {
    if (!canEdit) return;
    
    try {
      setSaving(true);
      await jobsAPI.indentTask(jobId, taskId, direction);
      await loadTasks();
      toast.success(direction === 'right' ? 'Task indented' : 'Task outdented');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change indent');
    } finally {
      setSaving(false);
    }
  };

  const handleCellEdit = async (taskId, field, value) => {
    try {
      const updateData = { [field]: value };
      
      if (field === 'given_work' || field === 'percent_complete') {
        updateData[field] = parseFloat(value) || 0;
      }
      
      await jobsAPI.updateTask(jobId, taskId, updateData);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updateData } : t));
    } catch (error) {
      toast.error('Failed to update task');
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e, taskId, field, value) => {
    if (e.key === 'Enter') {
      handleCellEdit(taskId, field, value);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleCellEdit(taskId, field, value);
      const fields = ['title', 'given_work', 'percent_complete', 'planned_start', 'planned_end', 'actual_start', 'actual_end'];
      const currentIndex = fields.indexOf(field);
      if (currentIndex < fields.length - 1) {
        setEditingCell({ taskId, field: fields[currentIndex + 1] });
      }
    }
  };

  const handleContextMenu = (e, task) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, task });
  };

  // Resource management
  const handleAddResource = async () => {
    if (!newResource.member_id) {
      toast.error('Please select a team member');
      return;
    }
    
    try {
      setSaving(true);
      await jobsAPI.addTaskResource(jobId, selectedTaskId, newResource);
      await loadTasks();
      setNewResource({ member_id: '', planned_hours: 0 });
      setShowAddResource(false);
      toast.success('Resource added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add resource');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveResource = async (resourceId) => {
    if (!window.confirm('Remove this resource?')) return;
    
    try {
      setSaving(true);
      await jobsAPI.removeTaskResource(jobId, selectedTaskId, resourceId);
      await loadTasks();
      toast.success('Resource removed');
    } catch (error) {
      toast.error('Failed to remove resource');
    } finally {
      setSaving(false);
    }
  };

  // Material management
  const handleAddMaterial = async () => {
    if (!newMaterial.description.trim()) {
      toast.error('Please enter material description');
      return;
    }
    
    try {
      setSaving(true);
      await jobsAPI.addTaskMaterial(jobId, selectedTaskId, newMaterial);
      await loadTasks();
      setNewMaterial({ category: 'Materials', description: '', quantity: 0, unit: 'each', rate: 0 });
      setShowAddMaterial(false);
      toast.success('Material added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add material');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMaterial = async (materialId) => {
    if (!window.confirm('Remove this material?')) return;
    
    try {
      setSaving(true);
      await jobsAPI.removeTaskMaterial(jobId, selectedTaskId, materialId);
      await loadTasks();
      toast.success('Material removed');
    } catch (error) {
      toast.error('Failed to remove material');
    } finally {
      setSaving(false);
    }
  };

  // Dependency management (Phase 4)
  const loadAvailablePredecessors = async (taskId) => {
    try {
      setLoadingPredecessors(true);
      const res = await jobsAPI.getAvailablePredecessors(jobId, taskId);
      setAvailablePredecessors(res.data.predecessors || []);
    } catch (error) {
      console.error('Failed to load available predecessors');
      setAvailablePredecessors([]);
    } finally {
      setLoadingPredecessors(false);
    }
  };

  const handleAddDependency = async () => {
    if (!newDependency.predecessor_id) {
      toast.error('Please select a predecessor task');
      return;
    }
    
    try {
      setSaving(true);
      await jobsAPI.addTaskDependency(jobId, selectedTaskId, newDependency);
      await loadTasks();
      setNewDependency({ predecessor_id: '', dependency_type: 'FS', lag_days: 0 });
      setShowAddDependency(false);
      toast.success('Dependency added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dependency');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDependency = async (predecessorId) => {
    if (!window.confirm('Remove this dependency?')) return;
    
    try {
      setSaving(true);
      await jobsAPI.removeTaskDependency(jobId, selectedTaskId, predecessorId);
      await loadTasks();
      toast.success('Dependency removed');
    } catch (error) {
      toast.error('Failed to remove dependency');
    } finally {
      setSaving(false);
    }
  };

  const getDependencyTypeLabel = (type) => {
    const types = {
      'FS': 'Finish-to-Start',
      'FF': 'Finish-to-Finish',
      'SS': 'Start-to-Start',
      'SF': 'Start-to-Finish'
    };
    return types[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-ZA');
    } catch {
      return dateStr;
    }
  };

  // Gantt Chart calculations (Phase 5)
  const ganttData = useMemo(() => {
    const tasksWithDates = tasks.filter(t => t.planned_start && t.planned_end);
    if (tasksWithDates.length === 0) {
      // If no tasks have dates, create a default range of 4 weeks from today
      const today = new Date();
      const start = new Date(today);
      start.setDate(start.getDate() - 7); // Start 1 week ago
      const end = new Date(today);
      end.setDate(end.getDate() + 21); // End 3 weeks from now
      return { minDate: start, maxDate: end, totalDays: 28, hasData: false };
    }
    
    let minDate = null;
    let maxDate = null;
    
    tasksWithDates.forEach(task => {
      const start = new Date(task.planned_start);
      const end = new Date(task.planned_end);
      if (!minDate || start < minDate) minDate = new Date(start);
      if (!maxDate || end > maxDate) maxDate = new Date(end);
    });
    
    // Add padding (1 week before and after)
    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 7);
    
    const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
    
    return { minDate, maxDate, totalDays, hasData: true };
  }, [tasks]);

  const getTaskBarStyle = useCallback((task) => {
    if (!task.planned_start || !task.planned_end || !ganttData.minDate) {
      return null;
    }
    
    const start = new Date(task.planned_start);
    const end = new Date(task.planned_end);
    const minDate = ganttData.minDate;
    
    const startOffset = Math.ceil((start - minDate) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    const dayWidth = 30; // pixels per day
    const left = startOffset * dayWidth;
    const width = Math.max(duration * dayWidth, dayWidth); // minimum 1 day width
    
    return { left, width };
  }, [ganttData]);

  const generateDateHeaders = useCallback(() => {
    if (!ganttData.minDate || !ganttData.maxDate) return { months: [], days: [] };
    
    const months = [];
    const days = [];
    const currentDate = new Date(ganttData.minDate);
    let currentMonth = null;
    let monthStartIndex = 0;
    
    for (let i = 0; i <= ganttData.totalDays; i++) {
      const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      if (monthKey !== currentMonth) {
        if (currentMonth !== null) {
          months.push({
            label: new Date(ganttData.minDate.getTime() + (monthStartIndex * 24 * 60 * 60 * 1000)).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }),
            days: i - monthStartIndex
          });
        }
        currentMonth = monthKey;
        monthStartIndex = i;
      }
      
      days.push({
        date: new Date(currentDate),
        label: currentDate.getDate(),
        isWeekend,
        isToday: currentDate.toDateString() === new Date().toDateString()
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Add last month
    if (currentMonth !== null) {
      months.push({
        label: new Date(ganttData.minDate.getTime() + (monthStartIndex * 24 * 60 * 60 * 1000)).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }),
        days: ganttData.totalDays - monthStartIndex + 1
      });
    }
    
    return { months, days };
  }, [ganttData]);

  const renderCell = (task, field, width) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === field;
    let value = task[field];
    let displayValue = value;
    
    if (field === 'percent_complete') {
      displayValue = `${value || 0}%`;
    } else if (field.includes('start') || field.includes('end')) {
      displayValue = formatDate(value);
    }
    
    if (isEditing && canEdit) {
      const inputType = field.includes('start') || field.includes('end') ? 'date' : 
                       field === 'percent_complete' || field === 'given_work' ? 'number' : 'text';
      
      return (
        <input
          ref={inputRef}
          type={inputType}
          defaultValue={value || ''}
          className="w-full h-full px-2 py-1 border-2 border-blue-500 outline-none bg-white text-sm"
          style={{ width }}
          onBlur={(e) => handleCellEdit(task.id, field, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, task.id, field, e.target.value)}
          min={field === 'percent_complete' ? 0 : undefined}
          max={field === 'percent_complete' ? 100 : undefined}
          step={field === 'given_work' ? 0.5 : undefined}
        />
      );
    }
    
    return (
      <div 
        className={`px-2 py-1.5 truncate text-sm cursor-pointer hover:bg-slate-100 ${
          field === 'percent_complete' ? 'text-center' : ''
        }`}
        onClick={() => canEdit && setEditingCell({ taskId: task.id, field })}
        title={displayValue}
      >
        {displayValue || '-'}
      </div>
    );
  };

  const flatTasks = getFlattenedTasks();
  const selectedTask = getSelectedTask();
  const dateHeaders = generateDateHeaders();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div 
        className="bg-white w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-800 text-white">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">Program Module</h2>
            <span className="text-slate-300">|</span>
            <span className="text-slate-300">{jobCode}</span>
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1 ml-4">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white'
                }`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('gantt')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'gantt' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white'
                }`}
                data-testid="gantt-view-btn"
              >
                <BarChart3 className="w-4 h-4" />
                Gantt
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canEdit && (
              <button
                onClick={() => handleAddTask()}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="add-root-task-btn"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            )}
            <button
              onClick={() => setShowDetailPanel(!showDetailPanel)}
              className={`p-2 rounded-lg transition-colors ${showDetailPanel ? 'bg-blue-600' : 'hover:bg-slate-700'}`}
              title={showDetailPanel ? 'Hide Detail Panel' : 'Show Detail Panel'}
              data-testid="toggle-detail-panel"
            >
              {showDetailPanel ? <PanelRightClose className="w-5 h-5" /> : <PanelRightOpen className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="close-program-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        {canEdit && selectedTaskId && (
          <div className="flex items-center gap-2 px-6 py-2 bg-slate-100 border-b">
            <button
              onClick={() => handleIndent(selectedTaskId, 'right')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-slate-50"
              title="Indent (make child of above task)"
            >
              <ArrowRight className="w-4 h-4" />
              Indent
            </button>
            <button
              onClick={() => handleIndent(selectedTaskId, 'left')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-slate-50"
              title="Outdent (move up a level)"
            >
              <ArrowLeft className="w-4 h-4" />
              Outdent
            </button>
            <div className="w-px h-6 bg-slate-300 mx-2" />
            <button
              onClick={() => handleInsertRow(selectedTaskId, 'above')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" />
              Insert Above
            </button>
            <button
              onClick={() => handleInsertRow(selectedTaskId, 'below')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" />
              Insert Below
            </button>
            <button
              onClick={() => handleAddTask(selectedTaskId)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" />
              Add Subtask
            </button>
            <div className="w-px h-6 bg-slate-300 mx-2" />
            <button
              onClick={() => handleDeleteTask(selectedTaskId)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Task Grid / Gantt View */}
          <div className={`flex-1 overflow-auto ${showDetailPanel ? 'border-r' : ''}`}>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : viewMode === 'list' ? (
              <div className="min-w-max">
                {/* Table Header */}
                <div className="flex items-center bg-slate-700 text-white text-sm font-medium sticky top-0 z-10">
                  <div className="w-12 px-2 py-3 text-center border-r border-slate-600">#</div>
                  <div className="w-72 px-2 py-3 border-r border-slate-600">Title</div>
                  <div className="w-24 px-2 py-3 text-center border-r border-slate-600">Work</div>
                  <div className="w-20 px-2 py-3 text-center border-r border-slate-600">% Done</div>
                  <div className="w-28 px-2 py-3 text-center border-r border-slate-600">Plan Start</div>
                  <div className="w-28 px-2 py-3 text-center border-r border-slate-600">Plan End</div>
                  <div className="w-28 px-2 py-3 text-center border-r border-slate-600">Act Start</div>
                  <div className="w-28 px-2 py-3 text-center border-r border-slate-600">Act End</div>
                  <div className="w-16 px-2 py-3 text-center border-r border-slate-600" title="Resources">
                    <Users className="w-4 h-4 mx-auto" />
                  </div>
                  <div className="w-16 px-2 py-3 text-center" title="Materials">
                    <Package className="w-4 h-4 mx-auto" />
                  </div>
                </div>

                {/* Table Body */}
                {flatTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                    <Clipboard className="w-12 h-12 mb-4 text-slate-300" />
                    <p className="text-lg">No tasks yet</p>
                    <p className="text-sm">Click "Add Task" to create your first task</p>
                  </div>
                ) : (
                  flatTasks.map((task) => {
                    const hasChildren = task.children && task.children.length > 0;
                    const isExpanded = expandedTasks.has(task.id);
                    const isSelected = selectedTaskId === task.id;
                    const isMilestone = task.is_milestone;
                    const isSummary = task.is_summary;
                    const resourceCount = (task.resources || []).length;
                    const materialCount = (task.materials || []).length;
                    
                    return (
                      <div
                        key={task.id}
                        data-testid={`task-row-${task.id}`}
                        className={`flex items-center border-b border-slate-200 hover:bg-slate-50 ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        } ${isSummary ? 'bg-slate-100 font-medium' : ''}`}
                        onClick={() => setSelectedTaskId(task.id)}
                        onContextMenu={(e) => handleContextMenu(e, task)}
                      >
                        {/* Row Number */}
                        <div className="w-12 px-2 py-2 text-center text-sm text-slate-500 border-r border-slate-200">
                          {task.rowNum}
                        </div>
                        
                        {/* Title with hierarchy */}
                        <div className="w-72 border-r border-slate-200 flex items-center">
                          <div style={{ width: task.level * 20 }} className="flex-shrink-0" />
                          {hasChildren ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                              className="p-1 hover:bg-slate-200 rounded mr-1"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                              )}
                            </button>
                          ) : (
                            <div className="w-6" />
                          )}
                          {isMilestone && <Flag className="w-4 h-4 text-amber-500 mr-1 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            {renderCell(task, 'title', '100%')}
                          </div>
                        </div>
                        
                        {/* Given Work */}
                        <div className="w-24 border-r border-slate-200 text-center">
                          {renderCell(task, 'given_work', 96)}
                        </div>
                        
                        {/* % Complete with progress bar */}
                        <div className="w-20 border-r border-slate-200 relative">
                          <div 
                            className="absolute inset-0 bg-green-100"
                            style={{ width: `${task.percent_complete || 0}%` }}
                          />
                          <div className="relative">
                            {renderCell(task, 'percent_complete', 80)}
                          </div>
                        </div>
                        
                        {/* Dates */}
                        <div className="w-28 border-r border-slate-200">
                          {renderCell(task, 'planned_start', 112)}
                        </div>
                        <div className="w-28 border-r border-slate-200">
                          {renderCell(task, 'planned_end', 112)}
                        </div>
                        <div className="w-28 border-r border-slate-200">
                          {renderCell(task, 'actual_start', 112)}
                        </div>
                        <div className="w-28 border-r border-slate-200">
                          {renderCell(task, 'actual_end', 112)}
                        </div>
                        
                        {/* Resource count */}
                        <div className="w-16 px-2 py-1.5 text-center border-r border-slate-200">
                          {resourceCount > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                              <User className="w-3 h-3" />
                              {resourceCount}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </div>
                        
                        {/* Material count */}
                        <div className="w-16 px-2 py-1.5 text-center">
                          {materialCount > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                              <Package className="w-3 h-3" />
                              {materialCount}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              /* Gantt Chart View */
              <div className="min-w-max" data-testid="gantt-view">
                {flatTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                    <BarChart3 className="w-12 h-12 mb-4 text-slate-300" />
                    <p className="text-lg">No tasks yet</p>
                    <p className="text-sm">Click "Add Task" to create your first task</p>
                  </div>
                ) : (
                  <div className="flex">
                    {/* Task List Panel (Left Side) */}
                    <div className="flex-shrink-0 w-64 border-r border-slate-300 bg-white">
                      {/* Task List Header */}
                      <div className="h-16 bg-slate-700 text-white flex items-end">
                        <div className="px-3 py-2 font-medium text-sm">Task Name</div>
                      </div>
                      {/* Task List */}
                      {flatTasks.map((task) => {
                        const isSelected = selectedTaskId === task.id;
                        const hasChildren = task.children && task.children.length > 0;
                        const isExpanded = expandedTasks.has(task.id);
                        
                        return (
                          <div
                            key={task.id}
                            className={`h-10 flex items-center border-b border-slate-200 cursor-pointer hover:bg-slate-50 ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            } ${task.is_summary ? 'bg-slate-100 font-medium' : ''}`}
                            onClick={() => setSelectedTaskId(task.id)}
                          >
                            <div style={{ width: task.level * 16 }} className="flex-shrink-0" />
                            {hasChildren ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                className="p-0.5 hover:bg-slate-200 rounded mr-1"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-3 h-3 text-slate-500" />
                                ) : (
                                  <ChevronRight className="w-3 h-3 text-slate-500" />
                                )}
                              </button>
                            ) : (
                              <div className="w-4" />
                            )}
                            {task.is_milestone && <Flag className="w-3 h-3 text-amber-500 mr-1 flex-shrink-0" />}
                            <span className="text-sm truncate pr-2" title={task.title}>{task.title}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Gantt Chart Panel (Right Side) */}
                    <div className="flex-1 overflow-x-auto">
                      {/* Timeline Header */}
                      <div className="sticky top-0 z-10 bg-slate-700">
                        {/* Month Row */}
                        <div className="h-8 flex border-b border-slate-600">
                          {dateHeaders.months.map((month, idx) => (
                            <div
                              key={idx}
                              className="text-white text-xs font-medium flex items-center justify-center border-r border-slate-600"
                              style={{ width: month.days * 30 }}
                            >
                              {month.label}
                            </div>
                          ))}
                        </div>
                        {/* Day Row */}
                        <div className="h-8 flex">
                          {dateHeaders.days.map((day, idx) => (
                            <div
                              key={idx}
                              className={`w-[30px] text-xs flex items-center justify-center border-r border-slate-600 ${
                                day.isToday ? 'bg-blue-600 text-white font-bold' : 
                                day.isWeekend ? 'bg-slate-600 text-slate-400' : 'text-slate-300'
                              }`}
                            >
                              {day.label}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Gantt Bars */}
                      <div className="relative" style={{ width: ganttData.totalDays * 30 }}>
                        {/* Grid Lines */}
                        <div className="absolute inset-0 flex pointer-events-none">
                          {dateHeaders.days.map((day, idx) => (
                            <div
                              key={idx}
                              className={`w-[30px] border-r ${
                                day.isToday ? 'border-blue-400 bg-blue-50/30' : 
                                day.isWeekend ? 'bg-slate-100/50 border-slate-200' : 'border-slate-200'
                              }`}
                              style={{ height: flatTasks.length * 40 }}
                            />
                          ))}
                        </div>
                        
                        {/* Task Bars */}
                        {flatTasks.map((task, taskIdx) => {
                          const barStyle = getTaskBarStyle(task);
                          const isSelected = selectedTaskId === task.id;
                          const hasDates = task.planned_start && task.planned_end;
                          const percentComplete = task.percent_complete || 0;
                          
                          return (
                            <div
                              key={task.id}
                              className={`h-10 relative border-b border-slate-200 ${isSelected ? 'bg-blue-50/50' : ''}`}
                              onClick={() => setSelectedTaskId(task.id)}
                            >
                              {hasDates && barStyle ? (
                                <div
                                  className={`absolute top-2 h-6 rounded cursor-pointer transition-all ${
                                    task.is_milestone ? 'w-4 h-4 top-3 rotate-45 bg-amber-500' :
                                    task.is_summary ? 'bg-slate-600' : 'bg-blue-500'
                                  } ${isSelected ? 'ring-2 ring-blue-300 ring-offset-1' : ''}`}
                                  style={{
                                    left: barStyle.left,
                                    width: task.is_milestone ? 12 : barStyle.width
                                  }}
                                  title={`${task.title}\n${formatDate(task.planned_start)} - ${formatDate(task.planned_end)}\n${percentComplete}% complete`}
                                >
                                  {/* Progress fill */}
                                  {!task.is_milestone && percentComplete > 0 && (
                                    <div
                                      className={`absolute inset-y-0 left-0 rounded-l ${
                                        task.is_summary ? 'bg-slate-800' : 'bg-blue-700'
                                      }`}
                                      style={{ width: `${percentComplete}%` }}
                                    />
                                  )}
                                  {/* Task label on bar */}
                                  {!task.is_milestone && barStyle.width > 60 && (
                                    <span className="absolute inset-0 flex items-center px-2 text-white text-xs truncate">
                                      {task.title}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="absolute inset-y-0 left-4 flex items-center">
                                  <span className="text-xs text-slate-400 italic">No dates set</span>
                                </div>
                              )}
                              
                              {/* Dependency arrows */}
                              {(task.dependencies || []).map((dep) => {
                                const predTask = tasks.find(t => t.id === dep.predecessor_id);
                                if (!predTask || !predTask.planned_end || !task.planned_start) return null;
                                
                                const predBarStyle = getTaskBarStyle(predTask);
                                const taskBarStyle = getTaskBarStyle(task);
                                if (!predBarStyle || !taskBarStyle) return null;
                                
                                const predIdx = flatTasks.findIndex(t => t.id === predTask.id);
                                if (predIdx === -1) return null;
                                
                                const predEndX = predBarStyle.left + predBarStyle.width;
                                const taskStartX = taskBarStyle.left;
                                const predY = (predIdx - taskIdx) * 40 + 20;
                                
                                return (
                                  <svg
                                    key={dep.predecessor_id}
                                    className="absolute pointer-events-none"
                                    style={{
                                      left: Math.min(predEndX, taskStartX) - 10,
                                      top: Math.min(0, predY) + 20,
                                      width: Math.abs(taskStartX - predEndX) + 20,
                                      height: Math.abs(predY) + 10,
                                      overflow: 'visible'
                                    }}
                                  >
                                    <path
                                      d={`M ${predEndX - Math.min(predEndX, taskStartX) + 10} ${predY < 0 ? Math.abs(predY) : 0} 
                                          L ${predEndX - Math.min(predEndX, taskStartX) + 15} ${predY < 0 ? Math.abs(predY) : 0}
                                          L ${predEndX - Math.min(predEndX, taskStartX) + 15} ${predY < 0 ? 0 : predY}
                                          L ${taskStartX - Math.min(predEndX, taskStartX) + 5} ${predY < 0 ? 0 : predY}`}
                                      fill="none"
                                      stroke="#94a3b8"
                                      strokeWidth="1.5"
                                      markerEnd="url(#arrowhead)"
                                    />
                                  </svg>
                                );
                              })}
                            </div>
                          );
                        })}
                        
                        {/* Arrow marker definition */}
                        <svg className="absolute" style={{ width: 0, height: 0 }}>
                          <defs>
                            <marker
                              id="arrowhead"
                              markerWidth="6"
                              markerHeight="6"
                              refX="5"
                              refY="3"
                              orient="auto"
                            >
                              <polygon points="0 0, 6 3, 0 6" fill="#94a3b8" />
                            </marker>
                          </defs>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {showDetailPanel && (
            <div className="w-80 bg-slate-50 overflow-y-auto" data-testid="detail-panel">
              {selectedTask ? (
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-4 truncate" title={selectedTask.title}>
                    {selectedTask.title}
                  </h3>
                  
                  {selectedTask.is_milestone ? (
                    <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
                      <Flag className="w-4 h-4 inline mr-2" />
                      Milestone tasks cannot have resources or materials
                    </div>
                  ) : (
                    <>
                      {/* Resources Section */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            Resources
                          </h4>
                          {canEdit && (
                            <button
                              onClick={() => setShowAddResource(!showAddResource)}
                              className="text-sm text-blue-600 hover:text-blue-700"
                              data-testid="add-resource-btn"
                            >
                              + Add
                            </button>
                          )}
                        </div>
                        
                        {showAddResource && canEdit && (
                          <div className="bg-white p-3 rounded-lg border mb-3">
                            <select
                              value={newResource.member_id}
                              onChange={(e) => setNewResource({ ...newResource, member_id: e.target.value })}
                              className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                              data-testid="resource-member-select"
                            >
                              <option value="">Select team member...</option>
                              {teamMembers.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                            <div className="flex gap-2 mb-2">
                              <input
                                type="number"
                                value={newResource.planned_hours}
                                onChange={(e) => setNewResource({ ...newResource, planned_hours: parseFloat(e.target.value) || 0 })}
                                placeholder="Hours"
                                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                min="0"
                                step="0.5"
                                data-testid="resource-hours-input"
                              />
                              <span className="flex items-center text-sm text-slate-500">hrs</span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={handleAddResource}
                                disabled={saving}
                                className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                                data-testid="save-resource-btn"
                              >
                                Add
                              </button>
                              <button
                                onClick={() => { setShowAddResource(false); setNewResource({ member_id: '', planned_hours: 0 }); }}
                                className="px-3 py-1.5 border rounded-lg text-sm hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {(selectedTask.resources || []).length === 0 ? (
                          <p className="text-sm text-slate-400 italic">No resources assigned</p>
                        ) : (
                          <div className="space-y-2">
                            {(selectedTask.resources || []).map(resource => (
                              <div key={resource.id} className="flex items-center justify-between bg-white p-2 rounded-lg border">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{resource.member_name}</p>
                                    <p className="text-xs text-slate-500">{resource.planned_hours} hours</p>
                                  </div>
                                </div>
                                {canEdit && (
                                  <button
                                    onClick={() => handleRemoveResource(resource.id)}
                                    className="p-1 text-slate-400 hover:text-red-500"
                                    data-testid={`remove-resource-${resource.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Materials Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium flex items-center gap-2">
                            <Package className="w-4 h-4 text-amber-600" />
                            Materials
                          </h4>
                          {canEdit && (
                            <button
                              onClick={() => setShowAddMaterial(!showAddMaterial)}
                              className="text-sm text-amber-600 hover:text-amber-700"
                              data-testid="add-material-btn"
                            >
                              + Add
                            </button>
                          )}
                        </div>
                        
                        {showAddMaterial && canEdit && (
                          <div className="bg-white p-3 rounded-lg border mb-3 space-y-2">
                            {/* Category */}
                            <select
                              value={newMaterial.category}
                              onChange={(e) => setNewMaterial({ ...newMaterial, category: e.target.value })}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                              data-testid="material-category-select"
                            >
                              {boqCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                            
                            {/* Description */}
                            <input
                              type="text"
                              value={newMaterial.description}
                              onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })}
                              placeholder="Description"
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                              data-testid="material-description-input"
                            />
                            
                            {/* Qty and Unit */}
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={newMaterial.quantity}
                                onChange={(e) => setNewMaterial({ ...newMaterial, quantity: parseFloat(e.target.value) || 0 })}
                                placeholder="Qty"
                                className="w-20 px-3 py-2 border rounded-lg text-sm"
                                min="0"
                                data-testid="material-qty-input"
                              />
                              <select
                                value={newMaterial.unit}
                                onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                data-testid="material-unit-select"
                              >
                                <option value="each">each</option>
                                <option value="kg">kg</option>
                                <option value="m">m</option>
                                <option value="m2">m²</option>
                                <option value="m3">m³</option>
                                <option value="liters">liters</option>
                                <option value="bags">bags</option>
                                <option value="sheets">sheets</option>
                                <option value="rolls">rolls</option>
                              </select>
                            </div>
                            
                            {/* Rate */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">R</span>
                              <input
                                type="number"
                                value={newMaterial.rate}
                                onChange={(e) => setNewMaterial({ ...newMaterial, rate: parseFloat(e.target.value) || 0 })}
                                placeholder="Rate"
                                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                min="0"
                                step="0.01"
                                data-testid="material-rate-input"
                              />
                            </div>
                            
                            {/* Amount (calculated) */}
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-sm">
                              <span className="text-slate-500">Amount:</span>
                              <span className="font-medium">{formatCurrency(newMaterial.quantity * newMaterial.rate)}</span>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={handleAddMaterial}
                                disabled={saving}
                                className="flex-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                                data-testid="save-material-btn"
                              >
                                Add
                              </button>
                              <button
                                onClick={() => { setShowAddMaterial(false); setNewMaterial({ category: 'Materials', description: '', quantity: 0, unit: 'each', rate: 0 }); }}
                                className="px-3 py-1.5 border rounded-lg text-sm hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {(selectedTask.materials || []).length === 0 ? (
                          <p className="text-sm text-slate-400 italic">No materials assigned</p>
                        ) : (
                          <div className="space-y-2">
                            {(selectedTask.materials || []).map(material => (
                              <div key={material.id} className="bg-white p-2 rounded-lg border">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Package className="w-4 h-4 text-amber-600" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium truncate">{material.description || material.name}</p>
                                      <p className="text-xs text-slate-500">{material.category}</p>
                                    </div>
                                  </div>
                                  {canEdit && (
                                    <button
                                      onClick={() => handleRemoveMaterial(material.id)}
                                      className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0"
                                      data-testid={`remove-material-${material.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center justify-between text-xs text-slate-600 pl-10">
                                  <span>{formatNumber(material.quantity)} {material.unit} × {formatCurrency(material.rate || 0)}</span>
                                  <span className="font-medium text-slate-800">{formatCurrency(material.amount || 0)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Dependencies Section (Phase 4) */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-purple-600" />
                            Dependencies
                          </h4>
                          {canEdit && (
                            <button
                              onClick={() => {
                                setShowAddDependency(!showAddDependency);
                                if (!showAddDependency) {
                                  loadAvailablePredecessors(selectedTaskId);
                                }
                              }}
                              className="text-sm text-purple-600 hover:text-purple-700"
                              data-testid="add-dependency-btn"
                            >
                              + Add
                            </button>
                          )}
                        </div>
                        
                        {showAddDependency && canEdit && (
                          <div className="bg-white p-3 rounded-lg border mb-3 space-y-2">
                            {/* Predecessor Select */}
                            {loadingPredecessors ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                              </div>
                            ) : (
                              <>
                                <select
                                  value={newDependency.predecessor_id}
                                  onChange={(e) => setNewDependency({ ...newDependency, predecessor_id: e.target.value })}
                                  className="w-full px-3 py-2 border rounded-lg text-sm"
                                  data-testid="dependency-predecessor-select"
                                >
                                  <option value="">Select predecessor task...</option>
                                  {availablePredecessors.map(task => (
                                    <option key={task.id} value={task.id}>
                                      {'  '.repeat(task.level || 0)}{task.title}
                                    </option>
                                  ))}
                                </select>
                                
                                {/* Dependency Type */}
                                <div className="flex gap-2">
                                  <select
                                    value={newDependency.dependency_type}
                                    onChange={(e) => setNewDependency({ ...newDependency, dependency_type: e.target.value })}
                                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                    data-testid="dependency-type-select"
                                  >
                                    <option value="FS">Finish-to-Start (FS)</option>
                                    <option value="FF">Finish-to-Finish (FF)</option>
                                    <option value="SS">Start-to-Start (SS)</option>
                                    <option value="SF">Start-to-Finish (SF)</option>
                                  </select>
                                  <input
                                    type="number"
                                    value={newDependency.lag_days}
                                    onChange={(e) => setNewDependency({ ...newDependency, lag_days: parseInt(e.target.value) || 0 })}
                                    placeholder="Lag"
                                    className="w-20 px-3 py-2 border rounded-lg text-sm"
                                    min="0"
                                    data-testid="dependency-lag-input"
                                  />
                                </div>
                                
                                <p className="text-xs text-slate-500">
                                  {newDependency.lag_days > 0 ? `+ ${newDependency.lag_days} day${newDependency.lag_days > 1 ? 's' : ''} lag` : 'No lag'}
                                </p>
                                
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleAddDependency}
                                    disabled={saving || !newDependency.predecessor_id}
                                    className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
                                    data-testid="save-dependency-btn"
                                  >
                                    Add
                                  </button>
                                  <button
                                    onClick={() => { setShowAddDependency(false); setNewDependency({ predecessor_id: '', dependency_type: 'FS', lag_days: 0 }); }}
                                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-slate-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {(selectedTask.dependencies || []).length === 0 ? (
                          <p className="text-sm text-slate-400 italic">No dependencies defined</p>
                        ) : (
                          <div className="space-y-2">
                            {(selectedTask.dependencies || []).map(dep => {
                              const predTask = tasks.find(t => t.id === dep.predecessor_id);
                              return (
                                <div key={dep.predecessor_id} className="flex items-center justify-between bg-white p-2 rounded-lg border">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                      <Link2 className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium">{predTask?.title || dep.predecessor_title || 'Unknown task'}</p>
                                      <p className="text-xs text-slate-500">
                                        {getDependencyTypeLabel(dep.dependency_type)}
                                        {dep.lag_days > 0 && ` + ${dep.lag_days} day${dep.lag_days > 1 ? 's' : ''}`}
                                      </p>
                                    </div>
                                  </div>
                                  {canEdit && (
                                    <button
                                      onClick={() => handleRemoveDependency(dep.predecessor_id)}
                                      className="p-1 text-slate-400 hover:text-red-500"
                                      data-testid={`remove-dependency-${dep.predecessor_id}`}
                                    >
                                      <Link2Off className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 p-4">
                  <Users className="w-12 h-12 mb-2" />
                  <p className="text-sm text-center">Select a task to view and manage resources, materials & dependencies</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-slate-50 text-sm text-slate-600">
          <div className="flex items-center gap-4">
            <span>{flatTasks.length} tasks</span>
            <span className="text-slate-300">|</span>
            <span>
              {flatTasks.filter(t => t.percent_complete === 100).length} completed
            </span>
            {selectedTask && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-blue-600">
                  {(selectedTask.resources || []).length} resources, {(selectedTask.materials || []).length} materials, {(selectedTask.dependencies || []).length} dependencies
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>Click cell to edit</span>
            <span>|</span>
            <span>Tab to move between fields</span>
            <span>|</span>
            <span>Enter to save</span>
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <div 
            className="fixed bg-white border rounded-lg shadow-lg py-1 z-50 min-w-48"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { handleInsertRow(contextMenu.task.id, 'above'); setContextMenu(null); }}
            >
              <Plus className="w-4 h-4" /> Insert Row Above
            </button>
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { handleInsertRow(contextMenu.task.id, 'below'); setContextMenu(null); }}
            >
              <Plus className="w-4 h-4" /> Insert Row Below
            </button>
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { handleAddTask(contextMenu.task.id); setContextMenu(null); }}
            >
              <Plus className="w-4 h-4" /> Add Subtask
            </button>
            <div className="border-t my-1" />
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { handleIndent(contextMenu.task.id, 'right'); setContextMenu(null); }}
            >
              <ArrowRight className="w-4 h-4" /> Indent
            </button>
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { handleIndent(contextMenu.task.id, 'left'); setContextMenu(null); }}
            >
              <ArrowLeft className="w-4 h-4" /> Outdent
            </button>
            <div className="border-t my-1" />
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { setSelectedTaskId(contextMenu.task.id); setShowDetailPanel(true); setContextMenu(null); }}
            >
              <Users className="w-4 h-4" /> Manage Resources
            </button>
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { setSelectedTaskId(contextMenu.task.id); setShowDetailPanel(true); setContextMenu(null); }}
            >
              <Package className="w-4 h-4" /> Manage Materials
            </button>
            <div className="border-t my-1" />
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
              onClick={() => { handleDeleteTask(contextMenu.task.id); setContextMenu(null); }}
            >
              <Trash2 className="w-4 h-4" /> Delete Task
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramModal;
